import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  type CommandPaletteContextValue,
  CommandPaletteContext,
  HOME_ID
} from './context'
import type { CommandPaletteSection } from './types'

/** mount once at the auth boundary. owns the palette state, the section
 * registry, and the global ⌘K hotkey. consumers dispatch via
 * useCommandPalette(). domains contribute sections via
 * useRegisterPaletteSection(section).
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeId, setActiveId] = useState<string>(HOME_ID)
  const [search, setSearch] = useState('')
  const [sectionsRecord, setSectionsRecord] = useState<
    Record<string, CommandPaletteSection>
  >({})

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const isHotkey =
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'k' &&
        !event.shiftKey &&
        !event.altKey
      if (!isHotkey) return
      event.preventDefault()
      setIsOpen(value => {
        if (!value) setActiveId(HOME_ID)
        return !value
      })
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // clear the query when switching modes so filter state doesn't leak across
  useEffect(() => {
    setSearch('')
  }, [activeId])

  const open = useCallback((sectionId?: string) => {
    setActiveId(sectionId ?? HOME_ID)
    setIsOpen(true)
  }, [])

  /** clear the active section and search on close. the activeId effect
   * already clears search when activeId changes, but a HOME to HOME re-open
   * wouldn't trigger that, leaving stale search text behind. resetting
   * here means every reopen starts at home with an empty query.
   */
  const close = useCallback(() => {
    setIsOpen(false)
    setActiveId(HOME_ID)
    setSearch('')
  }, [])

  const drillInto = useCallback((sectionId: string) => {
    setActiveId(sectionId)
  }, [])

  const back = useCallback(() => {
    setActiveId(HOME_ID)
  }, [])

  const registerSection = useCallback((section: CommandPaletteSection) => {
    setSectionsRecord(prev => ({ ...prev, [section.id]: section }))
  }, [])

  const unregisterSection = useCallback((id: string) => {
    setSectionsRecord(prev => {
      if (!(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  const sections = useMemo(
    () => Object.values(sectionsRecord),
    [sectionsRecord]
  )

  const value: CommandPaletteContextValue = useMemo(
    () => ({
      isOpen,
      activeId,
      search,
      sections,
      open,
      close,
      drillInto,
      back,
      setSearch,
      registerSection,
      unregisterSection
    }),
    [
      isOpen,
      activeId,
      search,
      sections,
      open,
      close,
      drillInto,
      back,
      registerSection,
      unregisterSection
    ]
  )

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  )
}
